import React, { useState, useEffect } from "react";
import styles from "./FieldValidator.module.css";
import toolbarStyles from "../../CygnusFormBuilderToolbar.module.css";
import Select from "react-select";
import {
  FieldValidatorProps,
  getRegexValidation,
  getValidationMessage,
  setRegexValidation,
  setValidationMessage,
  toggleRequiredValidation,
} from "./FieldValidator.utils";

/**
 * Common regex validation options
 */
const regexOptions = [
  {
    label: "Email",
    value: "email",
    pattern: "^[\\w.-]+@[\\w.-]+\\.\\w{2,}$",
  },
  {
    label: "Contact Number",
    value: "contact",
    pattern: "^\\d{10}$",
  },
  {
    label: "SSN",
    value: "ssn",
    pattern: "^\\d{3}-\\d{2}-\\d{4}$",
  },
  {
    label: "ein",
    value: "ein",
    pattern: "^\d{2}-\d{7}$",
  },
  {
    label: "Custom (Enter your own pattern)",
    value: "custom",
    pattern: "",
  },
];

const FieldValidator: React.FC<FieldValidatorProps> = ({
  selectedField,
  updateField,
}) => {
  const [activeToggle, setActiveToggle] = useState<boolean>(false);
  useEffect(() => {
    const validations = selectedField?.validation ?? [];
    setActiveToggle(validations.some((v) => v.type === "required"));
  }, [selectedField]);

  const currentRegex = getRegexValidation(selectedField);

  const getSelectedRegexOption = () => {
    return (
      regexOptions.find((opt) => opt.pattern === currentRegex) ||
      regexOptions.find((opt) => opt.value === "custom")
    );
  };

  const [selectedOption, setSelectedOption] = useState(getSelectedRegexOption);

  useEffect(() => {
    // Sync dropdown when regex is changed from outside
    setSelectedOption(getSelectedRegexOption());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedField]);

  const handleRegexSelect = (option: any) => {
    setSelectedOption(option);
    if (option.value !== "custom") {
      updateField(setRegexValidation(selectedField, option.pattern));
    }
  };

  /**
   * A reusable toggle switch component used for required field toggles.
   */
  const Switch: React.FC<{
    isToggled: boolean;
    onClick: () => void;
  }> = ({ isToggled, onClick }) => {
    return (
      <label className="switch">
        <input
          type="checkbox"
          checked={isToggled}
          onChange={onClick}
          style={{ marginLeft: 10 }}
        />
        <span className="super-admin-slider super-admin-round"></span>
      </label>
    );
  };

  return (
    <>
      {/* Required Toggle */}
      <div className={styles.requirementContainer}>
        Required
        <Switch
          isToggled={activeToggle}
          onClick={() => {
            setActiveToggle(!activeToggle);
            updateField({ ...toggleRequiredValidation(selectedField) });
          }}
        />
      </div>

      {/* Regex Dropdown */}
      <div className={toolbarStyles.formGroup}>
        <label>Validation Type</label>
        <Select
          options={regexOptions}
          value={selectedOption}
          onChange={handleRegexSelect}
          classNamePrefix="react-select"
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />
      </div>

      {/* Show custom pattern input when "Custom" is selected */}
      {selectedOption?.value === "custom" && (
        <div className={toolbarStyles.formGroup}>
          <label>Custom Regex Pattern</label>
          <input
            type="text"
            className="form-control custom-input"
            placeholder="e.g. ^[A-Z]{3}$"
            value={getRegexValidation(selectedField)}
            onChange={(e) =>
              updateField(
                setRegexValidation(selectedField, e.target.value.trim())
              )
            }
          />
        </div>
      )}

      {/* Validation Message */}
      <div className={toolbarStyles.formGroup}>
        <label>Validation Message</label>
        <input
          type="text"
          className="form-control custom-input"
          value={getValidationMessage(selectedField)}
          onChange={(e) =>
            updateField(setValidationMessage(selectedField, e.target.value))
          }
        />
      </div>
    </>
  );
};

export default FieldValidator;
